import {
    createContext,
    useContext,
    FC,
    PropsWithChildren,
    useState,
    useEffect,
    useCallback,
    ReactNode,
    useRef,
} from 'react';

import { useDomStoredUniqueId } from '@hh.ru/magritte-common-dom-storage';

type FormHelperState = {
    wantToShowDescription: boolean;
    wantToShowErrorMessage: boolean;
    setChildrenHelperState?: (state: FormHelperState, id: string) => void;
};
const FormHelperStateContext = createContext<FormHelperState>({
    wantToShowDescription: false,
    wantToShowErrorMessage: false,
});

export const FormHelperStateProvider: FC<PropsWithChildren<FormHelperState>> = ({
    wantToShowDescription,
    wantToShowErrorMessage,
    setChildrenHelperState,
    children,
}) => {
    const { wantToShowDescription: parentWantToShowDescription, wantToShowErrorMessage: parentWantToShowErrorMwssage } =
        useContext(FormHelperStateContext);
    return (
        <FormHelperStateContext.Provider
            value={{
                wantToShowDescription: wantToShowDescription || parentWantToShowDescription,
                wantToShowErrorMessage: wantToShowErrorMessage || parentWantToShowErrorMwssage,
                setChildrenHelperState,
            }}
        >
            {children}
        </FormHelperStateContext.Provider>
    );
};

const doesWantToShowErrorMessage = (errorMessage: ReactNode, invalid: boolean, disabled: boolean) =>
    !!errorMessage && invalid && !disabled;
const isErrorVisible = (wantToShowErrorMessage: boolean, childrenErrorVisible: boolean) =>
    wantToShowErrorMessage && !childrenErrorVisible;

const doesWantToShowDescription = (
    description: ReactNode,
    invalid: boolean,
    errorMessage: ReactNode,
    disabled: boolean
) => !!description && (!(invalid && errorMessage) || disabled);

const isDescriptionVisible = (
    isWantToShowDescription: boolean,
    childrenErrorVisible: boolean,
    childrenDescriptionVisible: boolean,
    parentWantToShowErrorMessage: boolean
) => isWantToShowDescription && !childrenDescriptionVisible && !childrenErrorVisible && !parentWantToShowErrorMessage;

type UseFormHelperStateHook = (args: {
    description?: ReactNode;
    errorMessage?: ReactNode;
    invalid?: boolean;
    disabled?: boolean;
}) => {
    /** Флаг ФАКТИЧЕСКОГО отображения описания, зависит от состояния дочерних компонентов */
    descriptionVisible: boolean;
    /** Флаг ФАКТИЧЕСКОГО отображения ошибки, зависит от состояния дочерних компонентов */
    errorMessageVisible: boolean;
    setChildrenHelperState: (state: FormHelperState, id: string) => void;
    /**
     * Флаг ЖЕЛАЕМОГО отображения описания, не зависит от состояния дочерних компонентов и родительских компонентов,
     * зависит только от переданных пропсов. Используется для передачи в дочерние и родительские компоненты.
     * Можно было бы использовать флаг ФАКТИЧЕСКОГО состояния, но тогда при изменении состояния будут происходить
     * лишние ререндеры т.к. изменение ФАКТИЧЕСКОГО состояния родителя меняло бы ФАКТИЧЕСКОЕ состояние потомков,
     * которое в свою очередь может изменить ФАКТИЧЕСКОЕ состояние родителя и так далее.
     * */
    wantToShowErrorMessage: boolean;
    /** Флаг ЖЕЛАЕМОГО отображения описания */
    wantToShowDescription: boolean;
};

/**
 * Хук для управления состоянием подсказок формы
 * Управляет состоянием отображения подсказок в зависимости от переданных пропсов и состояния в дочерних компонентах
 * отвечающих за отображение подсказок. Кроме того передает родительским компонентам подсказок информацию о своем
 * состоянии и состоянии дочерних компонентов подсказок.
 *
 * Реализует следующую логику:
 * 1. Если у компонента есть описание и нет ошибки, то отображается описание.
 * 2. Если у компонента есть ошибка и он невалиден, то отображается ошибка.
 * 3. Если несколько компонентов-подсказок вложенны один в другой, то отображать подсказку или ошибку одновременно
 *      может только один из них.
 * 5. Если несколько компонентов-подсказок вложенны один в другой, то приоритет для отображения имеет компонент
 *      с наибольшей глубиной вложенности.
 * 6. Отображение ошибок имеет более высокий приоритет, чем отображение описаний полей
 *
 * Иными словами, если есть несколько компонентов вложенных один в другой, и хотя бы один из них имеет ошибку,
 * то отображается только ошибка у компонента с наибольшей глубиной вложенности (или у нескольких если
 * они находятся на одной глубине). Если нет ошибок, то отображается только описание у компонента с наибольшей
 * глубиной вложенности (или у нескольких если они находятся на одной глубине).
 */
export const useFormHelperState: UseFormHelperStateHook = ({
    description,
    errorMessage,
    invalid = false,
    disabled = false,
}) => {
    const id = useDomStoredUniqueId();
    const childrensWithVisibleErrors = useRef(new Set<string>());
    const childrensWithVisibleDescriptions = useRef(new Set<string>());
    const { setChildrenHelperState: stateChangeCallback, wantToShowErrorMessage: parentErrorVisible } =
        useContext(FormHelperStateContext);
    const wantToShowDescription = doesWantToShowDescription(description, invalid, errorMessage, disabled);
    const wantToShowErrorMessage = doesWantToShowErrorMessage(errorMessage, invalid, disabled);
    const [descriptionVisible, setDescriptionVisible] = useState(
        isDescriptionVisible(
            wantToShowDescription,
            childrensWithVisibleErrors.current.size > 0,
            childrensWithVisibleDescriptions.current.size > 0,
            parentErrorVisible
        )
    );
    const [errorMessageVisible, setErrorMessageVisible] = useState(
        isErrorVisible(wantToShowErrorMessage, childrensWithVisibleErrors.current.size > 0)
    );

    const updateState = useCallback(() => {
        const selfDescriptionVisible = isDescriptionVisible(
            wantToShowDescription,
            childrensWithVisibleErrors.current.size > 0,
            childrensWithVisibleDescriptions.current.size > 0,
            parentErrorVisible
        );
        const selfErrorVisible = isErrorVisible(wantToShowErrorMessage, childrensWithVisibleErrors.current.size > 0);
        setDescriptionVisible(selfDescriptionVisible);
        setErrorMessageVisible(selfErrorVisible);
        stateChangeCallback?.(
            {
                wantToShowDescription: wantToShowDescription || childrensWithVisibleDescriptions.current.size > 0,
                wantToShowErrorMessage: wantToShowErrorMessage || childrensWithVisibleErrors.current.size > 0,
            },
            id
        );
    }, [wantToShowDescription, parentErrorVisible, wantToShowErrorMessage, stateChangeCallback, id]);

    useEffect(() => {
        updateState();
    }, [updateState]);

    const setChildrenHelperState = useCallback(
        (state: FormHelperState, id: string) => {
            const withVisibleErrorsCount = childrensWithVisibleErrors.current.size;
            const withVisibleDescriptionsCount = childrensWithVisibleDescriptions.current.size;
            /* eslint-disable @typescript-eslint/no-unused-expressions */
            state.wantToShowDescription
                ? childrensWithVisibleDescriptions.current.add(id)
                : childrensWithVisibleDescriptions.current.delete(id);
            state.wantToShowErrorMessage
                ? childrensWithVisibleErrors.current.add(id)
                : childrensWithVisibleErrors.current.delete(id);
            /* eslint-enable @typescript-eslint/no-unused-expressions */
            if (
                withVisibleErrorsCount !== childrensWithVisibleErrors.current.size ||
                withVisibleDescriptionsCount !== childrensWithVisibleDescriptions.current.size
            ) {
                updateState();
            }
        },
        [updateState]
    );

    return {
        descriptionVisible,
        errorMessageVisible,
        setChildrenHelperState,
        wantToShowErrorMessage,
        wantToShowDescription,
    };
};
